<script setup lang="ts">
import { isPortalFeatureEnabled } from '@/environment/environment'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import CustomDivider from '@/components/createdUi/CustomDivider.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import NewTreeComponent from '@/components/createdUi/NewTreeComponent.vue'
import { omitTreeIds, searchManagementDepartments } from '@/components/createdUi/treeSearch'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'
import type { ITreeNode } from '@/modules/apiStore/types/ITreeNode'
import { getFullName } from '@/utils/formatters'
import axios from 'axios'
import { useStaffSettingsStore } from '../../stores/staffSettingsStore'
import type { IStaffDepartmentManager, IStaffDepartmentTreeNode } from '../../types/settings/IStaffDepartmentSettings'

const props = defineProps<{
  modelValue: boolean
  tree: IStaffDepartmentTreeNode[]
  departmentId?: string | null
  parentId?: string | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  saved: []
}>()

const staffSettingsStore = useStaffSettingsStore()
const apiStore = useApiStore()

const name = ref('')
const shortName = ref('')
const description = ref('')
const parentId = ref<string | null>(null)
const isActive = ref(true)
const manager = ref<IStaffDepartmentManager | null>(null)
const saving = ref(false)
const fieldErrors = ref<Record<string, boolean>>({})
const employeeModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

const isEdit = computed(() => Boolean(props.departmentId))
const title = computed(() => isEdit.value ? 'Редактирование подразделения' : 'Создание подразделения')

const parentDepartmentTree = computed<ITreeNode[]>(() => {
  const excluded = excludedIds(props.tree, props.departmentId)

  function transform(nodes: IStaffDepartmentTreeNode[]): ITreeNode[] {
    return nodes
      .filter(node => !excluded.has(String(node.id)))
      .map(node => ({
        key: String(node.id),
        label: node.name,
        data: {
          name: node.name,
          selfId: String(node.id),
        },
        children: node.children?.length ? transform(node.children) : [],
      }))
  }

  return transform(props.tree)
})

async function searchParentDepartments(query: string): Promise<ITreeNode[]> {
  const tree = await searchManagementDepartments(query)
  return omitTreeIds(tree, excludedIds(props.tree, props.departmentId))
}

const managerName = computed(() => {
  if (!manager.value) return ''
  return manager.value.full_name || getFullName(manager.value)
})

watch(() => props.modelValue, async open => {
  if (open) {
    if (!staffSettingsStore.departmentsTree.length)
      await staffSettingsStore.fetchDepartmentsTree()
    fillForm()
  }
})

async function fillForm() {
  fieldErrors.value = {}
  name.value = ''
  shortName.value = ''
  description.value = ''
  parentId.value = props.parentId ?? null
  isActive.value = true
  manager.value = null
  if (!props.departmentId) return
  const detail = await staffSettingsStore.fetchDepartmentDetail(props.departmentId)
  if (!detail || !props.modelValue) return
  name.value = detail.name || ''
  shortName.value = detail.short_name || ''
  description.value = detail.description || ''
  parentId.value = detail.parent?.id
    ? String(detail.parent.id)
    : detail.parent_id
      ? String(detail.parent_id)
      : null
  isActive.value = detail.is_active !== false
  manager.value = detail.manager ?? null
}

function excludedIds(nodes: IStaffDepartmentTreeNode[], rootId?: string | null) {
  const ids = new Set<string>()
  if (!rootId) return ids
  function walk(list: IStaffDepartmentTreeNode[], ban: boolean) {
    for (const node of list) {
      const id = String(node.id)
      const next = ban || id === rootId
      if (next) ids.add(id)
      walk(node.children || [], next)
    }
  }
  walk(nodes, false)
  return ids
}

function close() {
  emit('update:modelValue', false)
}

async function openManagerPick() {
  if (!apiStore.employees?.length)
    await apiStore.fetchAllEmployees()
  employeeModalRef.value?.open()
}

function onManagerSelected(employee: IEmployeeTreeItem) {
  manager.value = {
    id: String(employee.id),
    full_name: employee.full_name,
    position_name: employee.position_name,
    avatar: null,
  }
}

async function submit() {
  fieldErrors.value = {}
  if (!name.value.trim()) {
    fieldErrors.value.name = true
    return
  }
  if (saving.value) return
  saving.value = true
  const payload = {
    name: name.value.trim(),
    short_name: shortName.value.trim(),
    description: description.value.trim(),
    parent_id: parentId.value || null,
    manager: manager.value ? { id: String(manager.value.id) } : null,
    is_active: isActive.value,
  }
  try {
    if (props.departmentId)
      await staffSettingsStore.updateDepartment(props.departmentId, payload)
    else
      await staffSettingsStore.createDepartment(payload)
    emit('saved')
    close()
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data
      if (Array.isArray(data?.errors)) {
        for (const item of data.errors) {
          const attr = item.attr || item.field
          if (attr)
            fieldErrors.value[attr] = true
        }
      } else if (data && typeof data === 'object') {
        for (const key of Object.keys(data)) {
          fieldErrors.value[key] = true
        }
      }
    }
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <VDialog
    :model-value="modelValue"
    max-width="640"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <VCard class="pa-6 d-flex flex-column ga-4 bg-surface">
      <div class="d-flex align-center justify-space-between ga-3">
        <span class="text-h4 staff-modal-title">{{ title }}</span>
        <CustomCloseButton :close-action="close" />
      </div>

      <VBtn v-if="departmentId && isPortalFeatureEnabled('electronic_signatures')" variant="tonal" prepend-icon="ri-key-2-line" :to="{ name: 'ELECTRONIC_SIGNATURES', params: { collection: 'requirements' }, query: { department: departmentId } }" @click="emit('update:modelValue', false)">Электронные подписи</VBtn>
      <div class="d-flex flex-column ga-4">
      <VTextField
        v-model="name"
        variant="outlined"
        density="comfortable"
        hide-details="auto"
        :error="Boolean(fieldErrors.name)"
        @update:model-value="delete fieldErrors.name"
      >
        <template #label>
          <span>Наименование подразделения<sup class="text-error">*</sup></span>
        </template>
      </VTextField>

      <VTextField
        v-model="shortName"
        variant="outlined"
        density="comfortable"
        hide-details
        :error="Boolean(fieldErrors.short_name)"
        @update:model-value="delete fieldErrors.short_name"
      >
        <template #label>
          <span>Краткое наименование</span>
        </template>
      </VTextField>

      <VTextarea
        v-model="description"
        class="flex-grow-0 flex-shrink-0"
        placeholder="Введите описание функций и задач отделения"
        variant="outlined"
        density="comfortable"
        persistent-placeholder
        rows="3"
        auto-grow
        hide-details
        :error="Boolean(fieldErrors.description)"
        @update:model-value="delete fieldErrors.description"
      >
        <template #label>
          <span>Описание</span>
        </template>
      </VTextarea>

      <NewTreeComponent
        label="Вышестоящее подразделение"
        placeholder="Не определено"
        search-placeholder="Поиск по отделениям"
        :selected="{ value: parentId ? { id: parentId } : null }"
        :tree-data="parentDepartmentTree"
        :search-tree="searchParentDepartments"
        @update:selected="parentId = $event ? String($event.id) : null"
      />

      <VBtn
        v-if="!manager"
        class="align-self-start"
        variant="outlined"
        color="primary"
        @click="openManagerPick"
      >
        Назначить руководителя подразделения
      </VBtn>
      <template v-else>
        <VChip
          class="align-self-start"
          color="primary"
          variant="tonal"
          size="small"
        >
          Руководитель
        </VChip>
        <div class="d-flex align-center ga-3">
          <div class="d-flex flex-column min-w-0">
            <span class="text-body-1 text-high-emphasis">{{ managerName }}</span>
            <span v-if="manager.position_name" class="text-body-2 text-medium-emphasis">
              {{ manager.position_name }}
            </span>
          </div>
          <VIconBtn
            class="ms-auto opacity-70 hover-opacity-100"
            size="small"
            variant="text"
            color="secondary"
            @click="manager = null"
          >
            <VIcon icon="ri-close-circle-fill" size="18" />
          </VIconBtn>
        </div>
        <CustomDivider />
      </template>

      <div class="d-flex align-center ga-3">
        <VSwitch
          v-model="isActive"
          hide-details
          density="compact"
          color="primary"
          inset
        />
        <div class="d-flex flex-column">
          <span class="text-body-1">Активность</span>
          <span class="text-body-2 text-medium-emphasis">
            (отображается в списках и доступно для прикрепления сотрудников)
          </span>
        </div>
      </div>

      <div class="d-flex justify-end">
        <VBtn
          color="primary"
          :loading="saving"
          :disabled="!name.trim()"
          @click="submit"
        >
          Сохранить
        </VBtn>
      </div>
      </div>
    </VCard>
  </VDialog>

  <EmployeeSelectionModal
    ref="employeeModalRef"
    :employees="apiStore.employees"
    title="Руководитель"
    @select="onManagerSelected"
  />
</template>

<style scoped lang="scss">
.staff-modal-title {
  color: rgba(var(--v-theme-text-primary), var(--v-high-emphasis-opacity));
}
</style>
